import { enforce, omitWhen, test } from 'vest';

import {
  isEndpointUrl,
  isTimeZone,
  withDefaults,
} from 'admin-panel/utils/integrations/config';

import type { IntegrationConfig } from 'admin-panel/models/integrations/configuration';
import type {
  IntegrationFormValues,
  ServiceFormDefinition,
} from 'admin-panel/utils/integrations/types';

export interface AsaRoomBillOptions {
  excludeRoomRate: boolean;
  spaDetailed: boolean;
  extrasDetailed: boolean;
  telDetailed: boolean;
  includeSettled: boolean;
}

export interface AsaService {
  reservationsApiEndpointUrl: string;
  timezone: string;
  checkInTime: string;
  checkOutTime: string;
  defaultGuestLastName: string;
  roomBillEnabled: boolean;
  roomBillOptions: AsaRoomBillOptions;
  authenticationMode: '' | 'http_basic';
  authenticationUsername: string;
  authenticationPassword: string;
  checkInConflictMode: 'error' | 'overwrite';
  reservationUpdateErrorMode: 'error' | 'discard';
}

export const ASA_AUTHENTICATION_MODES = ['', 'http_basic'];
export const ASA_CHECK_IN_CONFLICT_MODES = ['error', 'overwrite'];
export const ASA_RESERVATION_UPDATE_ERROR_MODES = ['error', 'discard'];

const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

const SERVICE = 'currentConfig.service';

const defaultConfig = (): IntegrationConfig => ({
  service: {
    reservationsApiEndpointUrl: '',
    timezone: '',
    checkInTime: '12:00',
    checkOutTime: '10:00',
    defaultGuestLastName: '',
    roomBillEnabled: false,
    roomBillOptions: {
      excludeRoomRate: false,
      spaDetailed: false,
      extrasDetailed: false,
      telDetailed: false,
      includeSettled: false,
    },
    authenticationMode: '',
    authenticationUsername: '',
    authenticationPassword: '',
    checkInConflictMode: 'error',
    reservationUpdateErrorMode: 'error',
  },
  features: {},
  cron: [],
  webhooks: [{ name: 'standard', enabled: true }],
});

export const asa: ServiceFormDefinition = {
  toFormConfig: (config) => withDefaults(defaultConfig(), config),

  validate(values: IntegrationFormValues, t) {
    const service = values.currentConfig.service as unknown as AsaService;

    test(`${SERVICE}.timezone`, t('validations.required'), () => {
      enforce(service.timezone).isNotBlank();
    });

    omitWhen(!service.timezone, () => {
      test(`${SERVICE}.timezone`, t('validations.invalid'), () => {
        enforce(isTimeZone(service.timezone)).isTruthy();
      });
    });

    for (const field of ['checkInTime', 'checkOutTime'] as const) {
      omitWhen(!service[field], () => {
        test(`${SERVICE}.${field}`, t('validations.invalid'), () => {
          enforce(service[field]).matches(TIME_OF_DAY);
        });
      });
    }

    omitWhen(!service.reservationsApiEndpointUrl, () => {
      test(
        `${SERVICE}.reservationsApiEndpointUrl`,
        t('validations.url'),
        () => {
          enforce(isEndpointUrl(service.reservationsApiEndpointUrl)).isTruthy();
        }
      );
    });

    test(`${SERVICE}.roomBillEnabled`, t('validations.required'), () => {
      enforce(service.roomBillEnabled).isBoolean();
    });

    omitWhen(!service.roomBillEnabled, () => {
      test(
        `${SERVICE}.reservationsApiEndpointUrl`,
        t('validations.required'),
        () => {
          enforce(service.reservationsApiEndpointUrl).isNotBlank();
        }
      );
    });

    test(`${SERVICE}.authenticationMode`, t('validations.invalid'), () => {
      enforce(service.authenticationMode).inside(ASA_AUTHENTICATION_MODES);
    });

    omitWhen(service.authenticationMode !== 'http_basic', () => {
      for (const field of [
        'authenticationUsername',
        'authenticationPassword',
      ] as const) {
        test(`${SERVICE}.${field}`, t('validations.required'), () => {
          enforce(service[field]).isNotBlank();
        });
      }
    });

    test(`${SERVICE}.checkInConflictMode`, t('validations.invalid'), () => {
      enforce(service.checkInConflictMode).inside(ASA_CHECK_IN_CONFLICT_MODES);
    });

    test(
      `${SERVICE}.reservationUpdateErrorMode`,
      t('validations.invalid'),
      () => {
        enforce(service.reservationUpdateErrorMode).inside(
          ASA_RESERVATION_UPDATE_ERROR_MODES
        );
      }
    );
  },
};
